import { useMemo } from 'react';

import type {
  BoundedTotal,
  JobSearchQuery,
} from '@xing-com/crate-common-graphql-types';
import {
  LazyLoading,
  LAZY_LOADING_TYPE_BUTTON,
} from '@xing-com/crate-entity-pages-common';
import { getTrackingUrl } from '@xing-com/crate-entity-pages-common/src/tracking';
import {
  JobsListItem,
  JobsEmptyState,
  checkBookmarkState,
} from '@xing-com/crate-entity-pages-jobs';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import type { JobsDetailResults } from '../../types';
import { JobHeader } from '../jobs-header/jobs-header';

import * as Styled from './jobs-detail.styles';

type RenderableJobItem = {
  bookmarkState: boolean;
  city: Array<string | null | undefined>;
  companyName: string;
  descriptionHighlight: string;
  id: string;
  paid?: boolean;
  refreshedAt: string;
  title: string;
  url: string;
};

type JobDetailTypeProps = {
  boundedTotal?: BoundedTotal | null;
  searchQuery?: Partial<JobSearchQuery>;
  jobResults?: JobsDetailResults;
  onLazyLoad: () => void;
  paginationLoading: boolean;
};

const getRenderableJobItems = (
  jobResults?: JobsDetailResults
): RenderableJobItem[] => {
  return (
    jobResults?.flatMap((jobResult) => {
      const jobDetail =
        jobResult?.jobDetail?.__typename === 'VisibleJob'
          ? jobResult.jobDetail
          : null;

      if (
        !jobDetail?.id ||
        !jobDetail.title ||
        !jobDetail.companyInfo.company?.companyName ||
        !jobDetail.refreshedAt
      ) {
        return [];
      }

      return [
        {
          bookmarkState: checkBookmarkState(
            jobDetail.userInteractions?.bookmark?.state
          ),
          city: jobDetail.locations?.map((location) => location?.city) || [],
          companyName: jobDetail.companyInfo.company.companyName,
          descriptionHighlight: jobResult?.descriptionHighlight ?? '',
          id: jobDetail.id,
          paid: jobDetail.paid,
          refreshedAt: jobDetail.refreshedAt,
          title: jobDetail.title,
          url: getTrackingUrl({
            to: jobDetail.url,
            trackingKey: 'viewJobProfile',
          }),
        },
      ];
    }) ?? []
  );
};

export const JobsDetailSkeleton: React.FC = () => {
  return (
    <Styled.Wrapper>
      <JobsListItem.Skeleton />
      <JobsListItem.Skeleton />
    </Styled.Wrapper>
  );
};

export const JobsDetail: React.FC<JobDetailTypeProps> = ({
  boundedTotal,
  searchQuery,
  jobResults,
  onLazyLoad,
  paginationLoading,
}) => {
  const { isLoggedIn } = useLoginState();

  const showDescription = !!searchQuery?.body?.keywords;

  const renderableJobItems = useMemo(
    () => getRenderableJobItems(jobResults),
    [jobResults]
  );

  const isPaginationEnabled = useMemo(() => {
    const isLimitReached =
      boundedTotal?.bound !== null &&
      Number(renderableJobItems.length) >= Number(boundedTotal?.bound);

    if (isLimitReached) {
      return true;
    }
    if (boundedTotal?.total === renderableJobItems.length) return false;
    return true;
  }, [boundedTotal, renderableJobItems.length]);

  if (renderableJobItems.length === 0) {
    return (
      <Styled.EmptyStateContainer>
        <JobsEmptyState statusCode={isLoggedIn ? undefined : 404} />
      </Styled.EmptyStateContainer>
    );
  }

  return (
    <Styled.Wrapper>
      {<JobHeader boundedTotal={boundedTotal?.total ?? 0} />}
      {renderableJobItems.map((jobItem) => {
        return (
          <JobsListItem
            activeUntil={undefined}
            key={jobItem.id}
            paid={jobItem.paid}
            title={jobItem.title}
            companyName={jobItem.companyName}
            city={jobItem.city}
            refreshedAt={jobItem.refreshedAt}
            url={jobItem.url}
            id={jobItem.id}
            bookmarkState={jobItem.bookmarkState}
            showBookmark={isLoggedIn}
            descriptionHighlight={jobItem.descriptionHighlight}
            showDescription={showDescription}
          />
        );
      })}
      <Styled.Pagination>
        <LazyLoading
          type={LAZY_LOADING_TYPE_BUTTON}
          isLoading={paginationLoading}
          enabled={isPaginationEnabled}
          onLazyLoad={onLazyLoad}
        />
      </Styled.Pagination>
    </Styled.Wrapper>
  );
};
